import { PropsWithChildren, useEffect, useRef } from 'react';

import {
  ChannelNames,
  ChannelTopics,
  Channels,
  ChainEvent,
  MessageBus,
  isChainableMessagePayload,
  isCallback,
  CallbackMessagePayload,
  ModalMode,
} from '@app-velocity/message-bus';

import { ModalContext } from '../context/ModalContext';
import { GetModalId } from '../types';
import { useModalWrapperContext } from '../context/useModalWrapperContext';
import { ModalPortal } from '../ModalPortal';

type ModalHandlerProps<T extends ChannelNames> = PropsWithChildren<{
  id: GetModalId<ChannelTopics[T]>;
  channel: T;
}>;

const dataContainsModalMode = (data: unknown): data is { mode: ModalMode } => {
  return !!(data as { mode: ModalMode }).mode;
};

const dataContainsClosable = (data: unknown): data is { closable: boolean } => {
  const parsed = data as { closable: boolean };

  return [true, false].includes(parsed.closable);
};

// @TODO - think of better name and separation of concerns here, it is not the best placement for the portal
export const ModalHandler = <T extends ChannelNames>({
  children,
  id,
  channel,
}: ModalHandlerProps<T>) => {
  const {
    openedModalId,
    openModal,
    closeModal,
    setModalData,
    setMode,
    setClosable,
  } = useModalWrapperContext();

  const messageBus = useRef(new MessageBus(channel));
  const chainedMessage = useRef<ChainEvent<ChannelNames> | undefined>(
    undefined
  );
  const closeCallback = useRef<
    CallbackMessagePayload['onModalCloseCallback'] | undefined
  >(undefined);

  const handleModalClose = (isSuccess?: boolean, data?: unknown) => {
    if (closeCallback.current) {
      closeCallback.current(isSuccess ? 'OK' : 'CANCEL', data);
    }

    if (chainedMessage.current && isSuccess) {
      const closeEvent = chainedMessage.current;

      if (!closeEvent) return;

      const tempBus = new MessageBus(closeEvent.channel);

      // @TODO: think of better idea here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tempBus.publish(closeEvent.topic, closeEvent.payload as any);

      return;
    }

    closeModal();
  };

  useEffect(() => {
    const unsubscribeOpen = messageBus.current.subscribe(
      `modal:${id}:open` as Channels[T]['topic'],
      (data: unknown) => {
        if (isChainableMessagePayload(data)) {
          chainedMessage.current = data.onModalCloseEvent;
        }

        if (isCallback(data)) {
          closeCallback.current = data.onModalCloseCallback;
        }

        if (data) {
          if (dataContainsModalMode(data)) {
            setMode(data.mode);
          } else {
            setMode('default');
          }

          if (dataContainsClosable(data)) {
            setClosable(data.closable);
          } else {
            setClosable(true);
          }

          setModalData(id, data);
        } else {
          setMode('default');
        }

        openModal(id);
      }
    );

    const unsubscribeClose = messageBus.current.subscribe(
      `modal:${id}:close` as Channels[T]['topic'],
      () => {
        closeModal();
      }
    );

    return () => {
      unsubscribeOpen();
      unsubscribeClose();
    };
  }, [channel, id, setModalData, openModal, closeModal, setMode]);

  if (openedModalId !== id) return false;

  return (
    <div>
      <ModalContext.Provider
        value={{
          closeModal: handleModalClose,
        }}
      >
        <ModalPortal>{children}</ModalPortal>
      </ModalContext.Provider>
    </div>
  );
};
